import React from 'react';
import {
  PhoneMailInteractItem,
  PhoneMailItem,
} from '../../types';
import {
  PhoneMailAttachmentsEditor,
  PhoneMailTriageMailsEditor,
} from './PhoneMailEditors';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

type MailItem = PhoneMailItem | PhoneMailInteractItem;

interface InlinePhoneMailEditorProps {
  item: MailItem;
  editMode: boolean;
  onChange: (item: MailItem) => void;
}

function MailReadonlySummary({ item }: { item: MailItem }) {
  const header = [item.from, item.subject].filter(Boolean).join(' — ');
  const isInteract = item.type === 'phoneMailInteract';
  const phone = !isInteract ? (item as PhoneMailItem) : null;

  return (
    <>
      {header && <span className={inline.slText}>{header}</span>}
      {item.preview && <span className={inline.mailPreview}>{item.preview}</span>}
      {item.content && <span className={inline.slText}>{item.content}</span>}
      {phone?.mails && phone.mails.length > 0 && (
        <span className={inline.mailMetaTag}>
          Session triage — {phone.mails.length} mail(s)
        </span>
      )}
      {phone?.triageActions && phone.triageActions.length > 0 && (
        <div className={inline.choiceRow}>
          {phone.triageActions.map((action, i) => (
            <span key={i} className={inline.choiceBtn}>
              <span className={inline.choiceBtnLabel}>{action.label || '…'}</span>
            </span>
          ))}
        </div>
      )}
      {phone?.goto && <span className={inline.choiceDest}>→ {phone.goto}</span>}
    </>
  );
}

function PhoneMailInteractFields({
  item,
  onChange,
}: {
  item: PhoneMailInteractItem;
  onChange: (item: PhoneMailInteractItem) => void;
}) {
  const patch = (partial: Partial<PhoneMailInteractItem>) =>
    onChange({ ...item, ...partial });

  return (
    <div className={inline.mailEditor}>
      <div className={inline.slMetaRow}>
        <div className={`${inline.metaField} ${inline.metaFieldGrow}`}>
          <span className={inline.metaFieldLabel}>De</span>
          <input
            className={studio.input}
            value={item.from ?? ''}
            onChange={(e) => patch({ from: e.target.value })}
            placeholder="Expéditeur <email@…>"
          />
        </div>
        <div className={`${inline.metaField} ${inline.metaFieldGrow}`}>
          <span className={inline.metaFieldLabel}>Objet</span>
          <input
            className={studio.input}
            value={item.subject ?? ''}
            onChange={(e) => patch({ subject: e.target.value })}
          />
        </div>
      </div>

      <div className={inline.slMetaRow}>
        <div className={`${inline.metaField} ${inline.metaFieldGrow}`}>
          <span className={inline.metaFieldLabel}>Aperçu (liste)</span>
          <input
            className={studio.input}
            value={item.preview ?? ''}
            onChange={(e) => patch({ preview: e.target.value || undefined })}
          />
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Heure</span>
          <input
            className={studio.input}
            value={item.receivedAt ?? ''}
            onChange={(e) => patch({ receivedAt: e.target.value || undefined })}
            placeholder="06:47"
          />
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Non lus</span>
          <input
            type="number"
            className={studio.input}
            value={item.unreadCount ?? ''}
            onChange={(e) =>
              patch({ unreadCount: parseInt(e.target.value, 10) || undefined })
            }
          />
        </div>
      </div>

      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Contenu</span>
        <textarea
          className={studio.textarea}
          rows={5}
          value={item.content ?? ''}
          onChange={(e) => patch({ content: e.target.value || undefined })}
        />
      </div>

      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Bouton fermer</span>
        <input
          className={studio.input}
          value={item.continueLabel ?? ''}
          onChange={(e) => patch({ continueLabel: e.target.value || undefined })}
          placeholder="Fermer"
        />
      </div>

      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Note pièces jointes</span>
        <input
          className={studio.input}
          value={item.attachmentsNote ?? ''}
          onChange={(e) => patch({ attachmentsNote: e.target.value || undefined })}
          placeholder="11 fichiers — le plus petit : 4,2 Mo"
        />
      </div>

      <div className={inline.mailNestedEditors}>
        <PhoneMailAttachmentsEditor
          attachments={item.attachments ?? []}
          onChange={(attachments) =>
            patch({ attachments: attachments.length > 0 ? attachments : undefined })
          }
        />
      </div>
    </div>
  );
}

function PhoneMailFields({
  item,
  onChange,
}: {
  item: PhoneMailItem;
  onChange: (item: PhoneMailItem) => void;
}) {
  const patch = (partial: Partial<PhoneMailItem>) => onChange({ ...item, ...partial });

  return (
    <div className={inline.mailEditor}>
      <PhoneMailTriageMailsEditor
        mails={item.mails ?? []}
        onChange={(mails) => patch({ mails: mails.length > 0 ? mails : undefined })}
      />
    </div>
  );
}

export function getPhoneMailTypeLabel(item: MailItem): string {
  return item.type === 'phoneMailInteract' ? 'Mail interactif (un seul mail)' : 'Mail téléphone (triage)';
}

export const InlinePhoneMailEditor: React.FC<InlinePhoneMailEditorProps> = ({
  item,
  editMode,
  onChange,
}) => {
  if (!editMode) {
    return <MailReadonlySummary item={item} />;
  }

  if (item.type === 'phoneMailInteract') {
    return (
      <PhoneMailInteractFields
        item={item}
        onChange={onChange}
      />
    );
  }

  return (
    <PhoneMailFields
      item={item}
      onChange={onChange}
    />
  );
};
